/**
 * A geometria do `FishNodeMap`: onde cada nó cai e por onde a trilha passa.
 *
 * ## Por que ela mora fora do `.vue`
 *
 * O jest deste cliente não transforma SFC (`jest.config.js` só tem `babel-jest` para `.js`), então
 * geometria dentro do componente é geometria que **ninguém testa** — e ela falha do pior jeito:
 * em silêncio. Nó sobreposto, trilha saindo pela diagonal errada, o enchimento começando na ponta
 * de trás — nada disso lança erro, só fica torto na tela. Aqui é função de entrada e saída.
 *
 * Tudo em **percentual do campo** (0 a 100 nos dois eixos), que é o `viewBox` do SVG: a mesma
 * geometria serve à roda de 320px do telefone e à de 480px do desktop sem recalcular nada.
 */

/** A coluna do nó no trilho. Sem ela, a primeira — a geometria nunca recebe `NaN`. */
export const colunaDe = (no) => no?.coluna ?? 1;

/** A fileira do nó no trilho: 0 em cima, 1 embaixo, 0.5 no meio (depois da junção). */
export const fileiraDe = (no) => no?.fileira ?? 0.5;

/**
 * Onde cada nó cai.
 *
 * **Cíclico:** um círculo de raio 38%, começando às 12h (`-90°`). Oito nós numa circunferência de
 * ~1005px sobram folgados a 56px cada (D19).
 *
 * **Trilho:** a coluna dá o x, a fileira dá o y. As duas pontas ficam com folga (8% e 92%) para o
 * nó não encostar na borda do campo.
 *
 * **Linear:** uma rota que serpenteia. Uma linha reta caberia, mas rota reta não parece viagem —
 * a curva é o que faz sete pontos lerem como travessia em vez de lista deitada.
 */
export function posicoesDosNos(arranjo, nos) {
  const total = nos.length;

  if (arranjo === 'ciclico') {
    return nos.map((_, i) => {
      const angulo = (i / total) * Math.PI * 2 - Math.PI / 2;
      return { x: 50 + Math.cos(angulo) * 38, y: 50 + Math.sin(angulo) * 38 };
    });
  }

  if (arranjo === 'trilho') {
    const colunas = Math.max(1, ...nos.map(colunaDe));
    return nos.map((no) => ({
      x: colunas === 1 ? 50 : 8 + ((colunaDe(no) - 1) / (colunas - 1)) * 84,
      y: 28 + fileiraDe(no) * 44,
    }));
  }

  return nos.map((_, i) => {
    const t = total === 1 ? 0.5 : i / (total - 1);
    return {
      // 6..94: numa tela de 360px cada ponto percentual é espaço entre paradas que se tocavam.
      x: 6 + t * 88,
      // A serpentina: onda e MEIA. Com duas ondas inteiras, os dois flancos de cada crista caíam
      // na MESMA altura e as paradas vizinhas se encostavam no telefone (conferido a 360px). Com
      // 1,5, sete paradas alternam 0/+A/0/−A — nenhum vizinho divide altura.
      y: 50 + Math.sin(t * Math.PI * 3) * 26,
    };
  });
}

/**
 * Os `d` do SVG — uma LISTA, porque o trilho tem dois (as duas fileiras que se juntam). No cíclico
 * o caminho FECHA (`Z`), no linear não — é a diferença inteira entre esses dois.
 *
 * Cada caminho vira um `<path>` próprio com `pathLength="100"`: assim os dois trilhos enchem **em
 * paralelo**, com o mesmo `dashoffset`. Num `d` só, com dois subcaminhos, o `pathLength` normaliza
 * o total e o segundo trilho só começaria a encher depois de o primeiro fechar.
 */
export function caminhosDaTrilha(arranjo, nos, posicoes = posicoesDosNos(arranjo, nos)) {
  if (!posicoes.length) return [];

  if (arranjo === 'trilho') {
    const indices = nos.map((_, i) => i);
    const porColuna = (a, b) => colunaDe(nos[a]) - colunaDe(nos[b]);
    // Os nós do meio são o tronco: as duas fileiras convergem no primeiro deles, que é a junção.
    const meio = indices.filter((i) => fileiraDe(nos[i]) === 0.5).sort(porColuna);
    return [0, 1]
      .map((fileira) => {
        const daFileira = indices.filter((i) => fileiraDe(nos[i]) === fileira).sort(porColuna);
        if (!daFileira.length) return '';
        const pontos = [...daFileira, ...meio].map((i) => posicoes[i]);
        // O trilho começa na borda esquerda: é o traço que o liga ao núcleo dos pontos.
        const passos = pontos.map((c) => `L ${c.x.toFixed(2)} ${c.y.toFixed(2)}`).join(' ');
        return `M 0 ${pontos[0].y.toFixed(2)} ${passos}`;
      })
      .filter(Boolean);
  }

  const d = posicoes
    .map((c, i) => `${i === 0 ? 'M' : 'L'} ${c.x.toFixed(2)} ${c.y.toFixed(2)}`)
    .join(' ');
  return [arranjo === 'ciclico' ? `${d} Z` : d];
}
